/**
 * usePendingCount — single shared poller for the navbar badge count.
 *
 * One poll per 30s, paused while the document is hidden. The first
 * poll fires immediately on mount so the badge appears as fast as the
 * first request can roundtrip; the timer takes over from there.
 *
 * Caller pattern (UniversalBottomNav):
 *   const count = usePendingCount();
 *   // → renders <Badge count={count}/> next to the tab icon
 *
 * The endpoint already returns 0 for members/visitors, so this hook
 * is safe to call unconditionally; it just won't paint anything.
 */
import { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL_MS = 30 * 1000;

const usePendingCount = () => {
  const { isAuthenticated, activeRole } = useAuth();
  const [count, setCount] = useState(0);
  // Refs so the polling/visibility handlers stay stable across renders
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    // Only poll for roles whose backend returns a meaningful number.
    // Member / scanner / visitor always get 0 — skip the network call.
    if (!isAuthenticated || !['leader', 'dg', 'admin'].includes(activeRole)) {
      setCount(0);
      return undefined;
    }

    let cancelled = false;

    const fetchOnce = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const res = await api.get('/appointments/pending-count');
        if (!cancelled) setCount(res.data?.count ?? 0);
      } catch (_) {
        // Silent — the badge isn't critical UX. Don't churn on errors.
      } finally {
        inFlightRef.current = false;
      }
    };

    const startTimer = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(fetchOnce, POLL_INTERVAL_MS);
    };
    const stopTimer = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopTimer();
      } else {
        // Re-fetch immediately when the user returns to the tab so the
        // number is fresh, then resume timer.
        fetchOnce();
        startTimer();
      }
    };

    // Initial fetch + timer
    fetchOnce();
    if (!document.hidden) startTimer();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      stopTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, activeRole]);

  return count;
};

export default usePendingCount;
